'use client';
import posthog, { PostHogConfig } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { ReactNode, useEffect } from 'react';
import { dlog } from '../../dev';
import { POSTHOG_CONFIG } from './posthogConfig';
import { DriftWindow } from 'src/window/driftWindow';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';

const MOBILE_WALLET_APP_NAMES = {
	PHANTOM: 'PHANTOM',
	SOLFLARE: 'SOLFLARE',
	BACKPACK: 'BACKPACK',
	GLOW: 'GLOW',
};

const ENABLE_PERSISTENCE = false;
const DEV_TEST_FLAG_LOADING_DELAY = false;

const isMobileWalletApp = () => {
	const userAgent = navigator.userAgent.toLowerCase();

	for (const mobileApp in MOBILE_WALLET_APP_NAMES) {
		if (userAgent.includes(mobileApp.toLowerCase())) {
			return true;
		}
	}

	return false;
};

const getOptionalPosthogParams = () => {
	const postHogParams: Partial<PostHogConfig> = {
		enable_recording_console_log: POSTHOG_CONFIG.consoleRecordingEnabled,
		session_recording: POSTHOG_CONFIG.sessionRecordingEnabled ? {} : undefined,
		capture_pageview: POSTHOG_CONFIG.capturePageViewEnabled,
		capture_pageleave: POSTHOG_CONFIG.capturePageViewEnabled,
		disable_session_recording: POSTHOG_CONFIG.sessionRecordingEnabled
			? undefined
			: true, // If session recording is enabled, then undefined so we ignore this config. If session recording DISABLED, then we set this to true. Session recording can be turned on again using posthog.startSessionRecording, controlled by some dev events
	};

	return postHogParams;
};

const alreadyEnabledPosthog = { current: false };

export default function PHProvider({ children }: { children: ReactNode }) {
	const appEventEmitter = useDriftStore((s) => s.appEventEmitter);
	const setDriftStore = useDriftStore((s) => s.set);

	useEffect(() => {
		if (alreadyEnabledPosthog.current) return;
		if (!appEventEmitter) return;

		dlog(`posthog`, `posthog_enabled`);

		alreadyEnabledPosthog.current = true;

		posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY ?? '', {
			api_host: '/ingest_posthog', // See next.config.js for more info about request re-routing to Posthog
			ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
			persistence: ENABLE_PERSISTENCE ? 'localStorage+cookie' : 'memory',
			loaded: (posthog) => {
				posthog.setPersonPropertiesForFlags({
					$current_url: window.location.href,
				});

				posthog.onFeatureFlags(async (flags) => {
					if (DEV_TEST_FLAG_LOADING_DELAY) {
						await new Promise((resolve) => setTimeout(resolve, 5_000));
					}

					dlog(`feature_flags`, `onFeatureFlags`, flags);

					appEventEmitter.emit('featureFlagsLoaded', flags);
				});

				// temp removing console error logging, posthog bills exploding because of this
				// Set up global error handler for console errors
				// const originalConsoleError = console.error;
				// console.error = (...args) => {
				// 	if (posthog.isFeatureEnabled('LOG_CONSOLE_ERRORS')) {
				// 		// Call original console.error
				// 		originalConsoleError.apply(console, args);
				// 		posthog.capture('console_error', {
				// 			error_message: args.map((arg) =>
				// 				arg instanceof Error
				// 					? { message: arg.message, stack: arg.stack }
				// 					: String(arg)
				// 			),
				// 			url: window.location.href,
				// 			timestamp: new Date().toISOString(),
				// 			//@ts-ignore
				// 			authority: window?.walletContext?.publicKey?.toString(),
				// 		});
				// 	}
				// };

				// Set up global unhandled error handler
				window.onerror = (message, source, lineno, colno, error) => {
					if (posthog.isFeatureEnabled('LOG_CONSOLE_ERRORS')) {
						posthog.capture('unhandled_error', {
							error_message: message,
							source,
							lineno,
							colno,
							stack: error?.stack,
							url: window.location.href,
							timestamp: new Date().toISOString(),
							//@ts-ignore
							authority: window?.walletContext?.publicKey?.toString(),
						});
					}
					return false;
				};

				// Set up unhandled promise rejection handler
				window.onunhandledrejection = (event) => {
					if (posthog.isFeatureEnabled('LOG_CONSOLE_ERRORS')) {
						posthog.capture('unhandled_promise_rejection', {
							error_message: event.reason?.message || String(event.reason),
							stack: event.reason?.stack,
							url: window.location.href,
							timestamp: new Date().toISOString(),
							//@ts-ignore
							authority: window?.walletContext?.publicKey?.toString(),
						});
					}
				};
			},
			autocapture: false,
			...getOptionalPosthogParams(),
		});
	}, [appEventEmitter]);

	useEffect(() => {
		const handler = (enabled: boolean) => {
			if (enabled) {
				dlog(`posthog`, `session_recording_enabled`);
				posthog.startSessionRecording();
			} else {
				dlog(`posthog`, `session_recording_disabled`);
				posthog.stopSessionRecording();
			}
		};

		DriftWindow.devEventEmitter.on('set_session_recording', handler);

		return () => {
			DriftWindow.devEventEmitter.off('set_session_recording', handler);
		};
	}, []);

	// Listen for changes to event capture setting
	useEffect(() => {
		const handleSetEventCapture = (enabled: boolean) => {
			dlog(`posthog`, `set_event_capture: ${enabled}`);
			setDriftStore((s) => {
				s.postHog.enabled = enabled;
			});
		};

		DriftWindow.devEventEmitter.on('set_event_capture', handleSetEventCapture);

		// Cleanup function to remove listener
		return () => {
			DriftWindow.devEventEmitter.off(
				'set_event_capture',
				handleSetEventCapture
			);
		};
	}, []);

	return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}

export const POSTHOG_UTILS = {
	isMobileWalletApp,
};
